import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll respond within 1–2 business days.");
      setForm(initial);
    } catch (err) {
      toast.error("Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#0B2B40] text-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="text-xs tracking-[0.3em] uppercase text-[#E8F0F8] font-medium mb-5">
              Reach out
            </div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight font-light">
              Begin a quiet <span className="italic">conversation.</span>
            </h2>
            <p className="mt-6 text-lg text-white/75 font-light leading-relaxed max-w-md">
              For partnerships, retreats, press, or coaching enquiries — Reza
              and the RBICA team will personally respond.
            </p>

            <div className="mt-12 space-y-6 text-white/85">
              <div className="flex items-center gap-4">
                <Mail size={18} className="text-[#E8F0F8]" />
                <a href="mailto:rbinnerrise@gmail.com" className="hover:text-white transition-colors">rbinnerrise@gmail.com</a>
              </div>
              <div className="flex items-center gap-4">
                <Phone size={18} className="text-[#E8F0F8]" />
                <a href="tel:+12534134604" className="hover:text-white transition-colors">+1 (253) 413-4604</a>
              </div>
              <div className="flex items-center gap-4">
                <MapPin size={18} className="text-[#E8F0F8]" />
                <span>Online, worldwide · In-person by appointment</span>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="contact-form"
            onSubmit={submit}
            className="lg:col-span-7 bg-white text-[#1A202C] p-8 md:p-12"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Name *</label>
                <input data-testid="contact-name-input" className="input-underline" value={form.name} onChange={handle("name")} />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Email *</label>
                <input data-testid="contact-email-input" type="email" className="input-underline" value={form.email} onChange={handle("email")} />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Phone</label>
                <input data-testid="contact-phone-input" className="input-underline" value={form.phone} onChange={handle("phone")} />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Subject</label>
                <input data-testid="contact-subject-input" className="input-underline" value={form.subject} onChange={handle("subject")} />
              </div>
            </div>
            <div className="mt-6">
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Your message *</label>
              <textarea data-testid="contact-message-input" className="input-underline" rows={5} value={form.message} onChange={handle("message")} />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="mt-10 bg-[#0B2B40] text-white hover:bg-[#1D4E89] disabled:opacity-60 px-9 py-4 text-sm font-medium tracking-wide transition-colors duration-300"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
